import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { BellRing, FileText } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useSendMarketingOptin, useSendUtilityMessage, useUtilityTemplatesQuery } from '@/queries/meta'

function IconAction({ label, icon, onClick }: { label: string; icon: React.ReactNode; onClick: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" onClick={onClick} aria-label={label}>
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

/** Utility template (`pages_utility_messaging`): writes outside the 24-hour window. */
function UtilityMessageDialog({ conversationId, open, onOpenChange, onSent }: {
  conversationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSent?: () => void
}) {
  const { t } = useTranslation()
  const templates = useUtilityTemplatesQuery(open)
  const send = useSendUtilityMessage(conversationId)
  const [name, setName] = React.useState('')
  const [params, setParams] = React.useState<string[]>([])
  const approved = (templates.data ?? []).filter((template) => template.status === 'APPROVED')
  const template = approved.find((candidate) => candidate.name === name)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!template) return
    send.mutate(
      { name: template.name, language: template.language, params: params.slice(0, template.param_count) },
      { onSuccess: () => onSent?.() }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={submit} className="space-y-4" noValidate>
          <DialogHeader>
            <DialogTitle>{t('messenger_outreach.utility.title')}</DialogTitle>
            <DialogDescription>{t('messenger_outreach.utility.description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="utility-template">{t('messenger_outreach.utility.template')}</Label>
            <Select value={name} onValueChange={(value) => { setName(value); setParams([]) }}>
              <SelectTrigger id="utility-template">
                <SelectValue placeholder={t('messenger_outreach.utility.template_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {approved.map((candidate) => (
                  <SelectItem key={candidate.name} value={candidate.name}>{candidate.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {templates.data && approved.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('messenger_outreach.utility.no_templates')}</p>
            )}
          </div>

          {template && <p className="text-sm text-muted-foreground">{template.body}</p>}

          {template && Array.from({ length: template.param_count }, (_, index) => (
            <div key={index} className="space-y-2">
              <Label htmlFor={`utility-param-${index}`}>{t('messenger_outreach.utility.param', { n: index + 1 })}</Label>
              <Input
                id={`utility-param-${index}`}
                aria-label={t('messenger_outreach.utility.param', { n: index + 1 })}
                value={params[index] ?? ''}
                onChange={(event) =>
                  setParams((current) => {
                    const next = [...current]
                    next[index] = event.target.value
                    return next
                  })
                }
              />
            </div>
          ))}

          {send.isError && (
            <Alert variant="destructive"><AlertDescription>{send.error.message}</AlertDescription></Alert>
          )}
          {send.isSuccess && (
            <Alert><AlertDescription>{t('messenger_outreach.utility.sent')}</AlertDescription></Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('messenger_outreach.close')}
            </Button>
            <Button
              type="submit"
              disabled={!template || send.isPending || params.slice(0, template.param_count).filter((p) => p?.trim()).length < template.param_count}
            >
              {t('messenger_outreach.utility.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Opt-in request for marketing messages (`marketing_messages_messenger`). */
function MarketingOptinDialog({ conversationId, open, onOpenChange, onSent }: {
  conversationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSent?: () => void
}) {
  const { t } = useTranslation()
  const send = useSendMarketingOptin(conversationId)
  const [title, setTitle] = React.useState(() => t('messenger_outreach.optin.default_title'))

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (title.trim()) send.mutate(title.trim(), { onSuccess: () => onSent?.() })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={submit} className="space-y-4" noValidate>
          <DialogHeader>
            <DialogTitle>{t('messenger_outreach.optin.title')}</DialogTitle>
            <DialogDescription>{t('messenger_outreach.optin.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="optin-title">{t('messenger_outreach.optin.question')}</Label>
            <Input
              id="optin-title"
              aria-label={t('messenger_outreach.optin.question')}
              maxLength={65}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          {send.isError && (
            <Alert variant="destructive"><AlertDescription>{send.error.message}</AlertDescription></Alert>
          )}
          {send.isSuccess && (
            <Alert><AlertDescription>{t('messenger_outreach.optin.sent')}</AlertDescription></Alert>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('messenger_outreach.close')}
            </Button>
            <Button type="submit" disabled={!title.trim() || send.isPending}>{t('messenger_outreach.optin.send')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Messenger-only conversation actions for writing outside the 24-hour window.
 * `onSent` lets the conversation reload its messages to show what was sent.
 */
export function MessengerOutreachActions({ conversationId, platform, onSent }: {
  conversationId: string
  platform: string
  onSent?: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState<'utility' | 'optin' | null>(null)
  if (platform !== 'facebook') return null

  return (
    <>
      <IconAction
        label={t('messenger_outreach.utility.button')}
        icon={<FileText className="h-4 w-4" />}
        onClick={() => setOpen('utility')}
      />
      <IconAction
        label={t('messenger_outreach.optin.button')}
        icon={<BellRing className="h-4 w-4" />}
        onClick={() => setOpen('optin')}
      />
      <UtilityMessageDialog
        conversationId={conversationId}
        open={open === 'utility'}
        onOpenChange={(value) => setOpen(value ? 'utility' : null)}
        onSent={onSent}
      />
      <MarketingOptinDialog
        conversationId={conversationId}
        open={open === 'optin'}
        onOpenChange={(value) => setOpen(value ? 'optin' : null)}
        onSent={onSent}
      />
    </>
  )
}
